import { writeFile } from "../../storage/fsa.ts";
import { renderIndexPage, type IndexPageOptions, type IndexColumn, type IndexRow } from "./index-page.ts";
import type { ModuleId } from "../../auth/config.ts";

/**
 * Writes a module's browsable output: the standalone `index.html` (opened
 * directly from a file manager) AND a machine-readable `index.json` sibling
 * that the app's own in-app viewer (src/ui/viewer/) reads back.
 *
 * The two come from one source — the same rows/columns — so the count and
 * contents the viewer shows can never drift from the standalone page. Every
 * module's index lives at `<moduleId>/index.{html,json}` (the on-disk module
 * directory name is always exactly the ModuleId).
 *
 * Kept out of index-page.ts so that file stays pure and DOM/fsa-free for its
 * unit tests; this one does the disk writes.
 */

/**
 * What each row's first-column link points at, so the viewer knows how to
 * show it without re-sniffing paths:
 *  - "html": a standalone HTML file to render inline (Teams transcript, OneNote page)
 *  - "file": a binary/opaque file to download or preview (an .eml, a OneDrive file)
 *  - "data": no file — everything shown is already in the row's cells
 *            (a calendar event, a contact, a task)
 */
export type ItemKind = "html" | "file" | "data";

export interface ModuleIndexData {
    moduleId: ModuleId;
    title: string;
    subtitle: string;
    /** How the viewer should present an item opened from this module. */
    itemKind: ItemKind;
    columns: readonly IndexColumn[];
    rows: readonly IndexRow[];
    generatedAt: string;
}

export interface ModuleIndexOptions extends IndexPageOptions {
    moduleId: ModuleId;
    itemKind: ItemKind;
}

export async function writeModuleIndex(
    root: FileSystemDirectoryHandle,
    options: ModuleIndexOptions
): Promise<void> {
    const { moduleId, itemKind, ...pageOptions } = options;

    await writeFile(root, `${moduleId}/index.html`, renderIndexPage(pageOptions));

    const data: ModuleIndexData = {
        moduleId,
        title: pageOptions.title,
        subtitle: pageOptions.subtitle,
        itemKind,
        columns: pageOptions.columns,
        rows: pageOptions.rows,
        generatedAt: new Date().toISOString()
    };
    await writeFile(root, `${moduleId}/index.json`, JSON.stringify(data));
}

export type { IndexColumn, IndexRow };
